'use client';

import { useCallback, useEffect, useState } from 'react';
import { summarizeCategoryChanges } from '@/lib/price-activity';
import type { PriceActivityCategorySummary } from '@/lib/price-activity-types';
import {
  getAllCategoryActivitySummaries,
  subscribePriceActivitySync,
} from '@/services/priceActivity.service';

const emptySummary = (category: string): PriceActivityCategorySummary =>
  summarizeCategoryChanges(category, []);

export function usePriceActivitySummaries(
  categories: string[],
  /** Incrementado após sync do catálogo para forçar releitura. */
  syncTick = 0
) {
  const categoriesKey = categories.join('|');
  const [summaries, setSummaries] = useState<Map<string, PriceActivityCategorySummary>>(
    () => new Map()
  );
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const ids = categoriesKey ? categoriesKey.split('|') : [];
    if (ids.length === 0) {
      setSummaries(new Map());
      setReady(true);
      return;
    }
    try {
      const next = await getAllCategoryActivitySummaries(ids);
      setSummaries(next);
    } catch {
      const fallback = new Map<string, PriceActivityCategorySummary>();
      for (const c of ids) fallback.set(c, emptySummary(c));
      setSummaries(fallback);
    } finally {
      setReady(true);
    }
  }, [categoriesKey]);

  useEffect(() => {
    // Subscrever antes do refresh para não perder o notify do sync.
    const unsub = subscribePriceActivitySync(() => {
      void refresh();
    });
    void refresh();
    return unsub;
  }, [refresh, syncTick]);

  const getSummary = useCallback(
    (category: string) => summaries.get(category) ?? emptySummary(category),
    [summaries]
  );

  return { summaries, getSummary, ready, refresh };
}
